/**
 * SelectWithCustom — string-label dropdown with a "Custom Value"
 * affordance.
 *
 * Mirrors the MoneySelect pattern but for plain text labels. The
 * dropdown shows:
 *
 *   • A list of preset items (id + label).
 *   • A "Custom Value" pill button at the bottom.
 *
 * Clicking the pill reveals an inline text input above it (the pill
 * stays visible). The user types, then commits via either:
 *   • Pressing Enter inside the input.
 *   • Clicking the pill a second time.
 * Either gesture (a) adds the trimmed value to the list, (b) selects
 * it, and (c) closes the popover. The popover also closes on a
 * regular option click or an outside click — those discard any draft
 * the user had typed.
 *
 * The component is uncontrolled w.r.t. the input draft (managed
 * locally) but controlled w.r.t. the selected value (caller passes
 * `value` + `onChange`). Custom values added during the session live
 * in component state so they appear alongside presets.
 */
import { useState } from "react";
import type { Key } from "react-aria-components";
import {
    Autocomplete as AriaAutocomplete,
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    ListBox as AriaListBox,
    Popover as AriaPopover,
} from "react-aria-components";
import { ChevronDown } from "@untitledui/icons";
import { Select, cx } from "@tricura/atlas-ui";

interface PresetItem {
    id: string;
    label: string;
}

interface SelectWithCustomProps {
    /** Currently selected id (matches a preset OR a custom-added id). */
    value: string;
    /** Fired when the user picks a preset OR confirms a custom entry. */
    onChange: (next: string) => void;
    /** Preset options shown at the top of the list. */
    presets: PresetItem[];
    placeholder?: string;
    isInvalid?: boolean;
    "aria-label"?: string;
    "data-autotest"?: string;
    dataAutotestPrefix?: string;
    /** Format a custom (non-preset) value for display only — e.g. append
     *  a "%" so a typed "10" reads as "10%" like the presets. The stored
     *  value (id / onChange) stays the raw string. Defaults to identity. */
    formatCustomLabel?: (raw: string) => string;
}

export function SelectWithCustom({
    value,
    onChange,
    presets,
    placeholder = "Select",
    isInvalid,
    "aria-label": ariaLabel,
    "data-autotest": dataAutotest,
    dataAutotestPrefix,
    formatCustomLabel,
}: SelectWithCustomProps) {
    /** Custom labels typed by the user this session — extras kept around
     *  so an earlier entry can be re-picked. The ACTIVE value is never
     *  sourced from here: it's derived from `value` at render (below), so
     *  it survives remounts, async hydration, and callers that normalize
     *  the committed string in onChange (OR-1347). */
    const [customs, setCustoms] = useState<string[]>([]);
    const [draftOpen, setDraftOpen] = useState(false);
    const [draft, setDraft] = useState("");

    const toLabel = (raw: string) => (formatCustomLabel ? formatCustomLabel(raw) : raw);
    const allItems: PresetItem[] = [...presets];
    // The current value ALWAYS gets an entry (right after the presets) when
    // it isn't one of them — this is what keeps the trigger label and the
    // list's checkmark in sync with whatever the caller actually stored.
    if (value && !allItems.some((i) => i.id === value)) {
        allItems.push({ id: value, label: toLabel(value) });
    }
    for (const c of customs) {
        const label = toLabel(c);
        // Dedupe by id AND label: a raw draft ("30%") and its normalized
        // stored value ("30") would otherwise render as twin entries.
        if (!allItems.some((i) => i.id === c || i.label === label)) {
            allItems.push({ id: c, label });
        }
    }
    const selectedItem = allItems.find((i) => i.id === value) ?? null;

    const commit = (raw: string, close: () => void) => {
        const trimmed = raw.trim();
        if (!trimmed) {
            setDraft("");
            return;
        }
        const inPreset = presets.some((p) => p.id === trimmed);
        if (!inPreset && !customs.includes(trimmed)) {
            setCustoms((prev) => [...prev, trimmed]);
        }
        onChange(trimmed);
        setDraft("");
        setDraftOpen(false);
        close();
    };

    return (
        <AriaDialogTrigger
            onOpenChange={(isOpen) => {
                // Reset the inline editor whenever the popover dismisses
                // — by selection, outside click, or Escape — so the next
                // open starts at the pill state.
                if (!isOpen) {
                    setDraftOpen(false);
                    setDraft("");
                }
            }}
        >
            <AriaButton
                aria-label={ariaLabel}
                data-autotest={dataAutotest}
                className={(state) =>
                    cx(
                        "bg-primary shadow-xs ring-primary outline-hidden relative flex h-9 w-full cursor-pointer items-center rounded-lg px-3 text-sm ring-1 ring-inset transition duration-100 ease-linear",
                        (state.isFocusVisible || state.isPressed) && "ring-brand ring-2",
                        state.isDisabled && "cursor-not-allowed opacity-50",
                        isInvalid && "ring-error_subtle",
                    )
                }
            >
                <span className={cx("flex w-full items-center truncate text-left")}>
                    {selectedItem ? (
                        <span className="text-primary truncate font-medium">{selectedItem.label}</span>
                    ) : (
                        <span className="text-placeholder">{placeholder}</span>
                    )}
                    <ChevronDown aria-hidden="true" className="size-4 text-fg-quaternary ml-auto shrink-0 stroke-[2.25px]" />
                </span>
            </AriaButton>

            <AriaPopover
                placement="bottom start"
                offset={4}
                className="w-(--trigger-width) bg-primary ring-secondary_alt outline-hidden overflow-hidden rounded-lg shadow-lg ring-1"
            >
                <AriaDialog className="outline-hidden">
                    {({ close }) => (
                        <>
                            <AriaAutocomplete>
                                <AriaListBox
                                    aria-label={ariaLabel || "Select a value"}
                                    items={allItems}
                                    selectionMode="single"
                                    selectedKeys={value ? new Set<Key>([value]) : new Set<Key>()}
                                    onSelectionChange={(keys) => {
                                        if (keys === "all") return;
                                        const k = keys.size > 0 ? Array.from(keys)[0] : null;
                                        if (k != null) {
                                            onChange(String(k));
                                            close();
                                        }
                                    }}
                                    className="outline-hidden max-h-72 overflow-y-auto py-1"
                                >
                                    {(item) => (
                                        <Select.Item
                                            id={item.id}
                                            data-autotest={dataAutotestPrefix ? `${dataAutotestPrefix}-option-${item.id.toLowerCase()}` : undefined}
                                        >
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </AriaListBox>
                            </AriaAutocomplete>

                            <div className="border-secondary flex flex-col gap-2 border-t p-3">
                                {draftOpen && (
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder="Enter Custom Value..."
                                        data-autotest={dataAutotestPrefix ? `${dataAutotestPrefix}-custom-value-input` : undefined}
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                commit(draft, close);
                                            } else if (e.key === "Escape") {
                                                e.preventDefault();
                                                setDraftOpen(false);
                                                setDraft("");
                                            }
                                        }}
                                        className="bg-primary text-primary ring-brand placeholder:text-placeholder w-full rounded-lg px-3 py-2 text-sm outline-none ring-2"
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        // First click reveals the inline input; subsequent
                                        // clicks (with the input visible) commit whatever's
                                        // currently typed. Empty draft is a no-op so the
                                        // pill behaves harmlessly when clicked twice.
                                        if (draftOpen) commit(draft, close);
                                        else setDraftOpen(true);
                                    }}
                                    data-autotest={dataAutotestPrefix ? `${dataAutotestPrefix}-custom-value-button` : undefined}
                                    className="bg-primary text-primary ring-secondary hover:bg-secondary inline-flex w-full cursor-pointer items-center justify-center rounded-full px-3.5 py-1.5 text-sm font-semibold ring-1 transition duration-100 ease-linear"
                                >
                                    Custom Value
                                </button>
                            </div>
                        </>
                    )}
                </AriaDialog>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}
